import React, { useEffect, useState } from 'react';
import { KeyRound, RefreshCw, ShieldAlert, Copy, Check, X, Clock, ChevronRight, Plus, Trash2, Server, Download, Terminal } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');
const th = { padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase' };
const td = { padding: '11px 12px', fontSize: 13, color: 'var(--ink)' };
const mono = { fontFamily: 'monospace', fontSize: 12 };
const OPEN_STATUS = {
  open: { label: 'Abierto', color: 'var(--amber)' },
  expired: { label: 'Expirado', color: 'var(--ink-3)' },
  rotated: { label: 'Rotado', color: 'var(--green)' },
  pending_approval: { label: 'Esperando aprobación', color: 'var(--amber)' },
  approved: { label: 'Aprobado', color: 'var(--green)' },
};

function Stat({ label, value, danger }) {
  return (
    <div style={{ flex: '1 1 130px', minWidth: 120, padding: '12px 14px', borderRight: '1px solid var(--border)' }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: danger && value > 0 ? 'var(--amber)' : 'var(--ink)', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>{label}</div>
    </div>
  );
}

export default function Pam() {
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});   // assetId → openings[]
  const [shown, setShown] = useState({});          // assetId → { username, password, expires_at }
  const [pending, setPending] = useState({});      // assetId → { opening_id } (crítico esperando firma)
  const [now, setNow] = useState(Date.now());
  const [bulkDays, setBulkDays] = useState(30);    // acción masiva de rotación
  const [ptab, setPtab] = useState('sobres');      // solapa: sobres break-glass | bóveda SSH

  const load = () => {
    setLoading(true);
    fetch(`${API_URL}/commands/local-admin`, { headers })
      .then(r => (r.ok ? r.json() : []))
      .then(l => setRows(Array.isArray(l) ? l : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (!Object.keys(shown).length) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [shown]);

  // Sobres críticos: mientras haya alguno esperando firma, poléamos su apertura. Al pasar a
  // 'approved' (un aprobador lo firmó en el teléfono) revelamos automáticamente la credencial.
  useEffect(() => {
    if (!Object.keys(pending).length) return;
    const tick = async () => {
      for (const [assetId, info] of Object.entries(pending)) {
        const hist = await fetch(`${API_URL}/commands/local-admin/${assetId}/openings`, { headers })
          .then(r => (r.ok ? r.json() : [])).catch(() => []);
        const op = hist.find(o => o.id === info.opening_id);
        if (!op) continue;
        if (op.status === 'approved') {
          const r = await fetch(`${API_URL}/commands/local-admin/${assetId}/reveal-approved`, { method: 'POST', headers });
          const d = await r.json().catch(() => ({}));
          if (r.ok) setShown(s => ({ ...s, [assetId]: d }));
          else alert(d.error || 'No se pudo revelar tras la aprobación.');
          setPending(p => { const n = { ...p }; delete n[assetId]; return n; });
          load(); if (expanded[assetId]) openHistory(assetId, true);
        } else if (op.status === 'expired' || op.status === 'rotated') {
          setPending(p => { const n = { ...p }; delete n[assetId]; return n; });
        }
      }
    };
    const t = setInterval(tick, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  const abrir = async (assetId) => {
    const reason = window.prompt('Motivo para abrir el sobre (queda auditado y se avisa a los admins):');
    if (!reason || reason.trim().length < 3) return;
    try {
      const r = await fetch(`${API_URL}/commands/local-admin/${assetId}/open`, {
        method: 'POST', headers, body: JSON.stringify({ reason: reason.trim() }) });
      const d = await r.json();
      if (r.status === 202) {
        // Sobre crítico: queda esperando que un aprobador lo firme en el teléfono. Poléamos.
        setPending(p => ({ ...p, [assetId]: { opening_id: d.opening_id } }));
        if (expanded[assetId]) openHistory(assetId, true);
      }
      else if (r.ok) { setShown(s => ({ ...s, [assetId]: d })); load(); if (expanded[assetId]) openHistory(assetId, true); }
      else alert(d.error || 'No se pudo abrir el sobre.');
    } catch { alert('Error de red.'); }
  };
  const toggleCritical = async (assetId, critical) => {
    const r = await fetch(`${API_URL}/commands/local-admin/${assetId}/criticality`, {
      method: 'PATCH', headers, body: JSON.stringify({ critical: !critical }) });
    if (r.ok) load(); else alert('Error.');
  };
  const openHistory = async (assetId, force) => {
    if (expanded[assetId] && !force) { setExpanded(e => { const n = { ...e }; delete n[assetId]; return n; }); return; }
    const h = await fetch(`${API_URL}/commands/local-admin/${assetId}/openings`, { headers }).then(r => r.ok ? r.json() : []).catch(() => []);
    setExpanded(e => ({ ...e, [assetId]: h }));
  };
  // Rotación programada: prende/apaga + intervalo (en días desde la UI → horas al backend).
  const setRotation = async (assetId, enabled, days) => {
    const body = { enabled, interval_hours: enabled ? Math.round(Number(days) * 24) : null };
    const r = await fetch(`${API_URL}/commands/local-admin/${assetId}/rotation`, { method: 'PATCH', headers, body: JSON.stringify(body) });
    if (r.ok) load(); else { const d = await r.json().catch(() => ({})); alert(d.error || 'Error.'); }
  };
  const bulkRotation = async (enabled) => {
    const days = Number(bulkDays);
    if (enabled && (!Number.isFinite(days) || days < 1)) { alert('Poné un intervalo válido en días.'); return; }
    const label = enabled ? `activar rotación cada ${days} día(s)` : 'desactivar la rotación';
    if (!window.confirm(`¿${label} en TODOS los sobres mostrados (${rows.length})?`)) return;
    const asset_ids = rows.map(c => c.asset_id);
    const body = { enabled, interval_hours: enabled ? Math.round(days * 24) : null, asset_ids };
    const r = await fetch(`${API_URL}/commands/local-admin/rotation/bulk`, { method: 'POST', headers, body: JSON.stringify(body) });
    if (r.ok) { const d = await r.json(); load(); alert(`Aplicado a ${d.updated} equipo(s).`); }
    else { const d = await r.json().catch(() => ({})); alert(d.error || 'Error.'); }
  };
  const rotDays = (c) => (c.rotate_interval_hours ? Math.round(c.rotate_interval_hours / 24) : 30);

  const isOpen = (c) => c.open_expires_at && new Date(c.open_expires_at).getTime() > now;
  const s = {
    total: rows.length,
    criticos: rows.filter(c => c.critical).length,
    abiertos: rows.filter(isOpen).length,
    autorot: rows.filter(c => c.auto_rotate_enabled).length,
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ background: '#E6F1FB', borderRadius: 10, padding: 8 }}><KeyRound size={20} color="var(--accent)" /></div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>PAM — Bóveda de credenciales</div>
            <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>Sobres break-glass del admin local + bóveda de credenciales SSH para servidores</div>
          </div>
        </div>
        {ptab === 'sobres' && (
          <button className="btn btn-secondary" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={14} /> Actualizar
          </button>
        )}
      </div>

      {/* Solapas */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
        {[{ id: 'sobres', label: 'Sobres break-glass' }, { id: 'ssh', label: 'Bóveda SSH' }].map(t => (
          <button key={t.id} onClick={() => setPtab(t.id)} style={{
            padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: 'none', border: 'none',
            borderBottom: ptab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
            color: ptab === t.id ? 'var(--accent)' : 'var(--ink-2)', marginBottom: -1,
          }}>{t.label}</button>
        ))}
      </div>

      {ptab === 'ssh' ? <SshVault /> : (
      <>
      <div className="card" style={{ padding: 0, display: 'flex', flexWrap: 'wrap', marginBottom: 16, overflow: 'hidden' }}>
        <Stat label="Sobres" value={s.total} />
        <Stat label="Críticos" value={s.criticos} />
        <Stat label="Abiertos ahora" value={s.abiertos} danger />
        <Stat label="Con auto-rotación" value={s.autorot} />
      </div>

      {/* Acción masiva de rotación */}
      {rows.length > 0 && (
        <div className="card" style={{ padding: '10px 14px', marginBottom: 16, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, color: 'var(--ink-2)', display: 'inline-flex', alignItems: 'center', gap: 6 }}><RefreshCw size={14} /> Rotación automática — aplicar a todos:</span>
          <span style={{ fontSize: 13, color: 'var(--ink)' }}>cada</span>
          <input type="number" min="1" value={bulkDays} onChange={e => setBulkDays(e.target.value)}
            style={{ width: 64, padding: '5px 8px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg)', color: 'var(--ink)' }} />
          <span style={{ fontSize: 13, color: 'var(--ink)' }}>día(s)</span>
          <button className="btn btn-primary btn-sm" onClick={() => bulkRotation(true)}>Activar en todos</button>
          <button className="btn btn-secondary btn-sm" onClick={() => bulkRotation(false)}>Desactivar en todos</button>
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)', fontSize: 14 }}>
            No hay sobres. Se crean con el admin local de respaldo (comando "Crear admin local").
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                <th style={th}>Equipo</th><th style={th}>Usuario</th><th style={th}>Estado</th><th style={th}>Última apertura</th><th style={th}>Rotación auto</th><th style={{ ...th, textAlign: 'right' }}>Acciones</th>
              </tr></thead>
              <tbody>
                {rows.map((c) => {
                  const open = isOpen(c);
                  const sh = shown[c.asset_id];
                  return (
                    <React.Fragment key={c.asset_id}>
                      <tr style={{ borderBottom: expanded[c.asset_id] ? 'none' : '1px solid var(--bg)' }}>
                        <td style={{ ...td, fontWeight: 600 }}>{c.hostname}</td>
                        <td style={{ ...td, ...mono }}>{c.username}</td>
                        <td style={td}>
                          {pending[c.asset_id] ? <span className="badge" style={{ color: 'var(--amber)' }}>esperando firma…</span>
                            : open ? <span className="badge" style={{ color: 'var(--amber)' }}>abierto</span>
                              : c.rotate_pending ? <span className="badge" style={{ color: 'var(--ink-3)' }}>rotación pendiente</span>
                                : <span className="badge" style={{ color: 'var(--green)' }}>sellado</span>}
                          {c.critical && <span className="badge" style={{ color: 'var(--red)', marginLeft: 6 }}>crítico</span>}
                        </td>
                        <td style={{ ...td, color: 'var(--ink-2)' }}>{c.revealed_at ? new Date(c.revealed_at).toLocaleString('es-AR') : '—'}</td>
                        <td style={td}>
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                            <input type="checkbox" checked={!!c.auto_rotate_enabled} onChange={e => setRotation(c.asset_id, e.target.checked, rotDays(c))} />
                            {c.auto_rotate_enabled ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--ink-2)' }}>
                                cada <input type="number" min="1" defaultValue={rotDays(c)} onBlur={e => { const v = Number(e.target.value); if (v >= 1 && v !== rotDays(c)) setRotation(c.asset_id, true, v); }}
                                  style={{ width: 48, padding: '3px 5px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 5, background: 'var(--bg)', color: 'var(--ink)' }} /> d
                              </span>
                            ) : <span style={{ color: 'var(--ink-3)' }}>off</span>}
                          </label>
                        </td>
                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button className="btn btn-primary btn-sm" onClick={() => abrir(c.asset_id)} disabled={!!pending[c.asset_id]} style={{ marginRight: 6 }}>{pending[c.asset_id] ? 'Esperando firma…' : 'Abrir sobre'}</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => toggleCritical(c.asset_id, c.critical)} style={{ marginRight: 6 }}>{c.critical ? 'quitar crítico' : 'marcar crítico'}</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => openHistory(c.asset_id)} title="Historial de aperturas"><ChevronRight size={13} style={{ transform: expanded[c.asset_id] ? 'rotate(90deg)' : 'none' }} /></button>
                        </td>
                      </tr>
                      {(sh || expanded[c.asset_id]) && (
                        <tr style={{ borderBottom: '1px solid var(--bg)', background: 'var(--bg-subtle)' }}>
                          <td colSpan={6} style={{ padding: '12px 16px' }}>
                            {sh && (() => {
                              const left = Math.max(0, Math.floor((new Date(sh.expires_at).getTime() - now) / 1000));
                              const mm = String(Math.floor(left / 60)).padStart(2, '0'), ss = String(left % 60).padStart(2, '0');
                              return (
                                <div style={{ marginBottom: expanded[c.asset_id] ? 12 : 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', color: 'var(--amber)' }}>
                                  <KeyRound size={13} /> <b>{sh.username}</b>:
                                  <code style={{ ...mono, fontWeight: 700 }}>{sh.password}</code>
                                  <button className="btn btn-secondary btn-sm" onClick={() => navigator.clipboard?.writeText(sh.password)}><Copy size={12} /> copiar</button>
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: left > 0 ? 'var(--amber)' : 'var(--red)' }}><Clock size={12} /> {left > 0 ? `expira ${mm}:${ss} · se rota luego` : 'ventana vencida — se está rotando'}</span>
                                  <button className="btn btn-secondary btn-sm" onClick={() => setShown(x => { const n = { ...x }; delete n[c.asset_id]; return n; })}><X size={12} /></button>
                                </div>
                              );
                            })()}
                            {expanded[c.asset_id] && (
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}><ShieldAlert size={13} /> Historial de aperturas</div>
                                {expanded[c.asset_id].length === 0 ? (
                                  <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Sin aperturas registradas.</div>
                                ) : (
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                                    <thead><tr><th style={th}>Cuándo</th><th style={th}>Quién</th><th style={th}>Motivo</th><th style={th}>Estado</th></tr></thead>
                                    <tbody>
                                      {expanded[c.asset_id].map(o => {
                                        const st = OPEN_STATUS[o.status] || { label: o.status, color: 'var(--ink-3)' };
                                        return (
                                          <tr key={o.id} style={{ borderTop: '1px solid var(--border)' }}>
                                            <td style={td}>{new Date(o.opened_at).toLocaleString('es-AR')}</td>
                                            <td style={{ ...td, color: 'var(--ink-2)' }}>{o.opened_by_name || '—'}</td>
                                            <td style={td}>{o.reason || '—'}</td>
                                            <td style={td}><span className="badge" style={{ color: st.color }}>{st.label}</span></td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}

const blankSsh = () => ({ label: '', group_name: '', hostname: '', port: 22, username: '', auth_type: 'key', secret: '', passphrase: '', note: '' });
const inSsh = { padding: '7px 9px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg)', color: 'var(--ink)', width: '100%' };
const slug = (s) => String(s || 'key').replace(/[^\w.-]/g, '_');

// Bóveda SSH: credenciales manuales para servers (AWS/otras nubes). Guardar cifrado + revelar
// (auditado) + ayudante de conexión (comando ssh, config, descarga de .pem).
function SshVault() {
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` };
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blankSsh());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [rev, setRev] = useState(null);       // credencial revelada (modal)
  const [copied, setCopied] = useState('');

  const load = () => {
    setLoading(true);
    fetch(`${API_URL}/pam-ssh`, { headers }).then(r => (r.ok ? r.json() : { credentials: [] }))
      .then(d => setList(Array.isArray(d.credentials) ? d.credentials : []))
      .catch(() => setList([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openAdd = () => { setEditId(null); setForm(blankSsh()); setErr(''); setShowForm(true); };
  const openEdit = (c) => { setEditId(c.id); setForm({ label: c.label, group_name: c.group_name || '', hostname: c.hostname, port: c.port, username: c.username, auth_type: c.auth_type, secret: '', passphrase: '', note: c.note || '' }); setErr(''); setShowForm(true); };

  const save = async () => {
    if (!form.label.trim() || !form.hostname.trim() || !form.username.trim()) { setErr('Completá nombre, host y usuario.'); return; }
    if (!editId && !form.secret) { setErr(form.auth_type === 'key' ? 'Pegá la clave privada.' : 'Ingresá la contraseña.'); return; }
    setSaving(true); setErr('');
    try {
      const body = { label: form.label.trim(), group_name: form.group_name.trim(), hostname: form.hostname.trim(), port: Number(form.port) || 22, username: form.username.trim(), auth_type: form.auth_type, note: form.note };
      if (form.secret) body.secret = form.secret;
      if (form.auth_type === 'key') body.passphrase = form.passphrase; else body.passphrase = '';
      const r = await fetch(editId ? `${API_URL}/pam-ssh/${editId}` : `${API_URL}/pam-ssh`, { method: editId ? 'PATCH' : 'POST', headers, body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Error');
      setShowForm(false); load();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };
  const del = async (c) => { if (!window.confirm(`¿Eliminar la credencial "${c.label}"?`)) return; const r = await fetch(`${API_URL}/pam-ssh/${c.id}`, { method: 'DELETE', headers }); if (r.ok) load(); else alert('Error eliminando.'); };
  const reveal = async (c) => { const r = await fetch(`${API_URL}/pam-ssh/${c.id}/reveal`, { method: 'POST', headers }); const d = await r.json().catch(() => ({})); if (r.ok) setRev(d); else alert(d.error || 'Error revelando.'); };

  const copy = (txt, tag) => { navigator.clipboard?.writeText(txt); setCopied(tag); setTimeout(() => setCopied(''), 1200); };
  const downloadKey = (r) => { const blob = new Blob([r.secret], { type: 'application/x-pem-file' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = slug(r.label) + '.pem'; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url); };
  const sshCmd = (r) => `ssh ${r.auth_type === 'key' ? `-i ${slug(r.label)}.pem ` : ''}${r.port && r.port !== 22 ? `-p ${r.port} ` : ''}${r.username}@${r.hostname}`;
  const sshConfig = (r) => `Host ${slug(r.label)}\n  HostName ${r.hostname}\n  User ${r.username}\n  Port ${r.port || 22}${r.auth_type === 'key' ? `\n  IdentityFile ~/.ssh/${slug(r.label)}.pem` : ''}`;

  const groups = {};
  for (const c of list) { const g = c.group_name || '(sin grupo)'; (groups[g] = groups[g] || []).push(c); }
  const groupNames = Object.keys(groups).sort();

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>Credenciales SSH cifradas en reposo. El secreto se revela solo con auditoría; te da el comando <code style={mono}>ssh</code>, el bloque de <code style={mono}>~/.ssh/config</code> y la descarga de la clave.</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={13} /></button>
          <button className="btn btn-primary btn-sm" onClick={openAdd} style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Plus size={13} /> Agregar credencial</button>
        </div>
      </div>

      {showForm && (
        <div className="card" style={{ padding: '16px 18px', marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: 'var(--ink)' }}>{editId ? 'Editar credencial' : 'Nueva credencial SSH'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 10 }}>
            <div><label style={lbl}>Nombre</label><input style={inSsh} value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="web-01 prod" /></div>
            <div><label style={lbl}>Grupo / nube</label><input style={inSsh} value={form.group_name} onChange={e => setForm(f => ({ ...f, group_name: e.target.value }))} placeholder="AWS" /></div>
            <div><label style={lbl}>Host / IP</label><input style={inSsh} value={form.hostname} onChange={e => setForm(f => ({ ...f, hostname: e.target.value }))} placeholder="1.2.3.4 o ec2-…" /></div>
            <div><label style={lbl}>Puerto</label><input style={inSsh} type="number" value={form.port} onChange={e => setForm(f => ({ ...f, port: e.target.value }))} /></div>
            <div><label style={lbl}>Usuario</label><input style={inSsh} value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} placeholder="ubuntu / ec2-user / root" /></div>
            <div><label style={lbl}>Autenticación</label>
              <select style={inSsh} value={form.auth_type} onChange={e => setForm(f => ({ ...f, auth_type: e.target.value }))}>
                <option value="key">Clave privada</option><option value="password">Usuario + contraseña</option>
              </select>
            </div>
          </div>
          {form.auth_type === 'key' ? (
            <>
              <label style={lbl}>Clave privada {editId && <span style={{ color: 'var(--ink-3)' }}>(dejar vacío = no cambiar)</span>}</label>
              <textarea style={{ ...inSsh, fontFamily: 'monospace', fontSize: 11, minHeight: 120 }} value={form.secret} onChange={e => setForm(f => ({ ...f, secret: e.target.value }))} placeholder="-----BEGIN OPENSSH PRIVATE KEY-----\n…" />
              <div style={{ marginTop: 8 }}><label style={lbl}>Passphrase de la clave (opcional)</label><input style={{ ...inSsh, maxWidth: 320 }} type="password" value={form.passphrase} onChange={e => setForm(f => ({ ...f, passphrase: e.target.value }))} /></div>
            </>
          ) : (
            <div><label style={lbl}>Contraseña {editId && <span style={{ color: 'var(--ink-3)' }}>(vacío = no cambiar)</span>}</label><input style={{ ...inSsh, maxWidth: 320 }} type="password" value={form.secret} onChange={e => setForm(f => ({ ...f, secret: e.target.value }))} /></div>
          )}
          <div style={{ marginTop: 8 }}><label style={lbl}>Nota (opcional)</label><input style={inSsh} value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} /></div>
          {err && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 8 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? '…' : (editId ? 'Guardar' : 'Agregar')}</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancelar</button>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : list.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)', fontSize: 14 }}>No hay credenciales SSH. Agregá la primera con "Agregar credencial".</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                <th style={th}>Credencial</th><th style={th}>Destino</th><th style={th}>Auth</th><th style={th}>Última revelación</th><th style={{ ...th, textAlign: 'right' }}>Acciones</th>
              </tr></thead>
              <tbody>
                {groupNames.map(g => (
                  <React.Fragment key={g}>
                    <tr style={{ background: 'var(--bg-subtle)' }}><td colSpan={5} style={{ padding: '6px 12px', fontSize: 11, fontWeight: 700, color: 'var(--ink-2)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}><Server size={12} /> {g} <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>({groups[g].length})</span></td></tr>
                    {groups[g].map(c => (
                      <tr key={c.id} style={{ borderBottom: '1px solid var(--bg)' }}>
                        <td style={{ ...td, fontWeight: 600 }}>{c.label}{c.note && <div style={{ fontWeight: 400, fontSize: 11, color: 'var(--ink-3)' }}>{c.note}</div>}</td>
                        <td style={{ ...td, ...mono, color: 'var(--ink-2)' }}>{c.username}@{c.hostname}{c.port !== 22 ? `:${c.port}` : ''}</td>
                        <td style={td}>{c.auth_type === 'key' ? <span className="badge" style={{ color: 'var(--accent)' }}>clave{c.has_passphrase ? ' +pass' : ''}</span> : <span className="badge" style={{ color: 'var(--ink-2)' }}>password</span>}</td>
                        <td style={{ ...td, color: 'var(--ink-2)', fontSize: 12 }}>{c.revealed_at ? `${new Date(c.revealed_at).toLocaleString('es-AR')}${c.revealed_by ? ` · ${c.revealed_by}` : ''}` : '—'}</td>
                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button className="btn btn-primary btn-sm" onClick={() => reveal(c)} style={{ marginRight: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Terminal size={12} /> Conectar</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => openEdit(c)} style={{ marginRight: 6 }}>editar</button>
                          <button className="btn btn-danger btn-sm" onClick={() => del(c)}><Trash2 size={12} /></button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {rev && (
        <div onClick={() => setRev(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} className="card" style={{ padding: '20px 22px', maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 7 }}><Terminal size={16} color="var(--accent)" /> {rev.label}</div>
              <button onClick={() => setRev(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)' }}><X size={16} /></button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 14 }}>Acceso auditado. No compartas la clave; borrá el .pem cuando termines.</div>

            <label style={lbl}>Comando</label>
            <div style={cmdBox}>
              <code style={{ ...mono, fontSize: 12.5, wordBreak: 'break-all' }}>{sshCmd(rev)}</code>
              <button className="btn btn-secondary btn-sm" onClick={() => copy(sshCmd(rev), 'cmd')}>{copied === 'cmd' ? <Check size={12} /> : <Copy size={12} />}</button>
            </div>

            {rev.auth_type === 'key' ? (
              <div style={{ margin: '12px 0' }}>
                <button className="btn btn-primary btn-sm" onClick={() => downloadKey(rev)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Download size={13} /> Descargar {slug(rev.label)}.pem</button>
                <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 8 }}>guardala en ~/.ssh/ y <code style={mono}>chmod 600</code></span>
                {rev.passphrase && (
                  <div style={{ ...cmdBox, marginTop: 10 }}>
                    <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>Passphrase:</span> <code style={{ ...mono, fontWeight: 700 }}>{rev.passphrase}</code>
                    <button className="btn btn-secondary btn-sm" onClick={() => copy(rev.passphrase, 'pass')}>{copied === 'pass' ? <Check size={12} /> : <Copy size={12} />}</button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ ...cmdBox, margin: '12px 0' }}>
                <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>Contraseña:</span> <code style={{ ...mono, fontWeight: 700 }}>{rev.secret}</code>
                <button className="btn btn-secondary btn-sm" onClick={() => copy(rev.secret, 'pwd')}>{copied === 'pwd' ? <Check size={12} /> : <Copy size={12} />}</button>
              </div>
            )}

            <label style={lbl}>~/.ssh/config</label>
            <div style={{ ...cmdBox, alignItems: 'flex-start' }}>
              <pre style={{ ...mono, fontSize: 12, margin: 0, whiteSpace: 'pre-wrap', flex: 1 }}>{sshConfig(rev)}</pre>
              <button className="btn btn-secondary btn-sm" onClick={() => copy(sshConfig(rev), 'cfg')}>{copied === 'cfg' ? <Check size={12} /> : <Copy size={12} />}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const lbl = { fontSize: 11, color: 'var(--ink-2)', display: 'block', marginBottom: 3, fontWeight: 600 };
const cmdBox = { display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px' };
